/**
 * PATH: apps/ai/src/pages/SimilarityPage.tsx
 * PURPOSE: Similarity search for jobs, quotes, and items
 */

import { useState } from 'react';
import { Search, FileText, Briefcase, Package, ArrowRight, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';

interface SearchResult {
  id: string;
  type: 'job' | 'quote' | 'item';
  title: string;
  description: string;
  similarity: number;
  metadata: Record<string, string | number>;
}

export default function SimilarityPage() {
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState<'all' | 'job' | 'quote' | 'item'>('all');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([
    {
      id: '1',
      type: 'job',
      title: 'Commercial HVAC Installation - Tech Park',
      description: 'Full HVAC system installation for 2000 sqm commercial office space including ducting, split systems, and building automation integration.',
      similarity: 0.94,
      metadata: { value: '$185,000', duration: '6 weeks', margin: '18%' },
    },
    {
      id: '2',
      type: 'quote',
      title: 'HVAC Upgrade - Industrial Warehouse',
      description: 'Upgrade existing HVAC system with high-efficiency units and improved ventilation for manufacturing floor.',
      similarity: 0.87,
      metadata: { value: '$92,000', status: 'Won', client: 'Acme Manufacturing' },
    },
    {
      id: '3',
      type: 'item',
      title: 'Split System AC Unit - 7.1kW',
      description: 'Commercial grade split system air conditioning unit with inverter technology.',
      similarity: 0.82,
      metadata: { price: '$2,450', supplier: 'HVAC Direct', usage: '45 times' },
    },
  ]);

  const handleSearch = () => {
    if (!query.trim()) return;
    
    setIsSearching(true);
    
    // Simulate search
    setTimeout(() => {
      const mockResults: SearchResult[] = [
        {
          id: Date.now().toString() + '1',
          type: 'job',
          title: `Related Job: ${query.slice(0, 30)}...`,
          description: 'Similar project found in your job history with matching scope and specifications.',
          similarity: 0.85 + Math.random() * 0.1,
          metadata: { value: `$${Math.floor(Math.random() * 200000)}`, duration: `${Math.floor(Math.random() * 12)} weeks` },
        },
        {
          id: Date.now().toString() + '2',
          type: 'quote',
          title: `Related Quote: ${query.slice(0, 25)}`,
          description: 'Quote with similar line items and scope found.',
          similarity: 0.75 + Math.random() * 0.15,
          metadata: { value: `$${Math.floor(Math.random() * 100000)}`, status: 'Pending' },
        },
        {
          id: Date.now().toString() + '3',
          type: 'item',
          title: 'Matching Item from Pricelist',
          description: 'Item matching your search criteria from your approved pricelist.',
          similarity: 0.7 + Math.random() * 0.2,
          metadata: { price: `$${Math.floor(Math.random() * 5000)}`, stock: 'Available' },
        },
      ];
      
      setResults((prev) => [...mockResults, ...prev]);
      setIsSearching(false);
    }, 1500);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'job':
        return <Briefcase className="w-5 h-5" />;
      case 'quote':
        return <FileText className="w-5 h-5" />;
      case 'item':
        return <Package className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'job':
        return 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400';
      case 'quote':
        return 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400';
      case 'item':
        return 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400';
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const getSimilarityColor = (similarity: number) => {
    if (similarity >= 0.9) return 'text-green-600';
    if (similarity >= 0.75) return 'text-orange-600';
    return 'text-yellow-600';
  };

  const filteredResults = searchType === 'all' 
    ? results 
    : results.filter((r) => r.type === searchType);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Similarity Search</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Find similar jobs, quotes, and items using AI-powered semantic search
        </p>
      </div>

      {/* Search Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg text-white">
            <Search className="w-5 h-5" />
          </div>
          <h2 className="font-semibold text-gray-900 dark:text-white">Find Similar</h2>
        </div>

        {/* Type Filter */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { value: 'all', label: 'All Types' },
            { value: 'job', label: 'Jobs' },
            { value: 'quote', label: 'Quotes' },
            { value: 'item', label: 'Items' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setSearchType(option.value as typeof searchType)}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                searchType === option.value
                  ? "bg-orange-500 text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Search Input */}
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Describe what you're looking for... e.g., 'Commercial HVAC installation for office building'"
              className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={!query.trim() || isSearching}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center gap-2"
          >
            {isSearching ? (
              <>
                <Sparkles className="w-5 h-5 animate-pulse" />
                Searching...
              </>
            ) : (
              <>
                <Search className="w-5 h-5" />
                Search
              </>
            )}
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 dark:text-white">
            Results ({filteredResults.length})
          </h2>
          <p className="text-sm text-gray-500">Sorted by similarity</p>
        </div>

        {filteredResults.map((result) => (
          <div
            key={result.id}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-4">
              {/* Type Icon */}
              <div className={cn("p-3 rounded-xl", getTypeColor(result.type))}>
                {getTypeIcon(result.type)}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                    {result.title}
                  </h3>
                  <span className={cn(
                    "text-sm font-bold",
                    getSimilarityColor(result.similarity)
                  )}>
                    {(result.similarity * 100).toFixed(0)}% match
                  </span>
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                  {result.description}
                </p>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(result.metadata).map(([key, value]) => (
                    <span
                      key={key}
                      className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-3 py-1 rounded-full"
                    >
                      <span className="capitalize">{key}:</span> {value}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action */}
              <button className="p-2 text-gray-400 hover:text-orange-500 transition-colors">
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            {/* Similarity Bar */}
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-16">Similarity</span>
                <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={cn(
                      "h-full rounded-full transition-all",
                      result.similarity >= 0.9
                        ? "bg-green-500"
                        : result.similarity >= 0.75
                        ? "bg-orange-500"
                        : "bg-yellow-500"
                    )}
                    style={{ width: `${result.similarity * 100}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-12">
                  {(result.similarity * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

